import { useCallback } from "react";

import {
  Locales,
  PaymentMethod,
  getAvailableAddOnCount,
  getField,
  parseFormBackgroundTheme,
  resolveBackgroundThemeCategory,
} from "@simplyk/common";
import { FieldValues } from "react-hook-form";

import type { AmplitudePaymentEventLogModel } from "./amplitudePaymentEventLogModel";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { isDonationForm } from "@/common/helpers/form";
import { getErrorMessages } from "@/common/helpers/reactHookForm";
import { getSuggestedAmountsTracking } from "@/common/helpers/suggestedAmountsTracking";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { PaymentElementProps } from "@/components/PaymentProcessor/type";

type LogDonorFormSubmittedParams = {
  isExpressCheckout: boolean;
  isPaymentSucceeded: boolean;
  errorCode?: string;
  errorMessage?: string;
  extra?: Record<string, unknown>;
};

type LogParams<T extends FieldValues> = Pick<PaymentElementProps<T>, "formStateErrors" | "formObject"> & {
  model: AmplitudePaymentEventLogModel;
};

export function useAmplitudePaymentEventLog<T extends FieldValues>({
  formStateErrors,
  formObject,
  model,
}: LogParams<T>) {
  const { logAmplitudeEvent } = useAmplitude();

  const {
    commandId,
    tip: rawTip,
    selectedPaymentMethod,
    isEmbed,
    category,
    formAmplitudeContext,
    displayedFormAmount,
    getTipChoice,
    selectedTip,
    displayAddressQuestion,
    organization,
    currency,
    ticketing,
    itemsCount,
    addOnsCount,
    locale,
    isoLocale,
    source,
    impactChainUtmSourceEmail,
    expressCheckoutWallet,
    isDonationFormOverride,
    suggestedAmountsExperimentData,
    getDonationSelection,
  } = model;

  const tip = selectedPaymentMethod === PaymentMethod.Daf ? 0 : rawTip;
  const availableAddOnsCount = "rates" in formObject ? getAvailableAddOnCount(formObject.rates) : 0;

  const isDonationForEvent = isDonationFormOverride ?? isDonationForm(formObject);
  const donationFormForAmplitude = isDonationForEvent && isDonationForm(formObject) ? formObject : undefined;

  const formStateErrorKeys = Object.keys(formStateErrors);
  const hasFormStateErrors = formStateErrorKeys.length > 0;
  const formStateErrorMessage = hasFormStateErrors
    ? getErrorMessages({ keys: formStateErrorKeys, errors: formStateErrors })
    : null;

  const logDonorFormSubmitted = useCallback(
    ({
      isExpressCheckout,
      errorCode: code,
      errorMessage: message,
      isPaymentSucceeded,
      extra,
    }: LogDonorFormSubmittedParams) => {
      const errorCode = hasFormStateErrors ? formStateErrorMessage : code;
      const donationSelection = getDonationSelection?.();
      const suggestedAmountsTracking = donationFormForAmplitude
        ? getSuggestedAmountsTracking(
            getField(donationFormForAmplitude.donationFormFields, locale as Locales)?.donationFormAmounts
          )
        : {};
      const errorMessage = hasFormStateErrors ? "form_error" : message;

      logAmplitudeEvent(AmplitudeEvents.DonorFormSubmitted, {
        ...formAmplitudeContext,
        ...extra,
        commandId,
        formType: category,
        errorCode,
        errorMessage,
        isPaymentSucceeded,
        countCustomQuestions: formObject.questions?.length,
        displayAddressQuestion,
        encourageMonthlyDonation: Boolean(donationFormForAmplitude?.encourageMonthlyDonations),
        hasALinkedTicketing: Boolean(donationFormForAmplitude?.redirectUrl),
        hasAdditionalDonation: Boolean(ticketing.extraDonation),
        hasBanner: Boolean(formObject.bannerUrl),
        hasBannerVideo: Boolean(formObject.bannerVideoUrl),
        hasCheckOption:
          isDonationForEvent && isDonationForm(formObject) ? formObject.chequeMinimumAmount !== null : false,
        hasDafOption: Boolean(donationFormForAmplitude?.dafEnabled),
        hasInHonourOption: Boolean(donationFormForAmplitude?.allowInHonour),
        hasLogo: Boolean(formObject.logoUrl),
        hasNoSuggestedAmount: formAmplitudeContext.hasNoSuggestedAmount || null,
        hasReceipt: Boolean(donationFormForAmplitude?.hasReceipt),
        hasReceiptMessage: formAmplitudeContext.hasReceiptMessage || null,
        hasTarget: Boolean(ticketing.thermometerTarget),
        isEmbed,
        isPrimaryCampaign: Boolean(donationFormForAmplitude?.isPrimaryCampaign),
        isPrimaryTeam: Boolean(donationFormForAmplitude?.isPrimaryTeam),
        isoLocale,
        locale,
        organizationCountry: organization?.country,
        tipAmount: tip / 100,
        tipChoice: selectedTip.percentage ? getTipChoice(String(selectedTip.percentage)) : "Custom",
        tipPercentage: displayedFormAmount > 0 ? ((tip / displayedFormAmount) * 100).toFixed(2) : "N A",
        formId: formObject.id,
        amount: displayedFormAmount / 100,
        totalAmount: (displayedFormAmount + tip) / 100,
        paymentMethod: selectedPaymentMethod,
        isExpressCheckout,
        currency,
        expressCheckoutWallet: isExpressCheckout ? expressCheckoutWallet : null,
        impact_chain: impactChainUtmSourceEmail ? true : false,
        has_communication_preferences: Boolean(formObject.communicationPreferences?.length),
        source,
        is_dark_mode: formObject.isDarkMode || false,
        background_category: resolveBackgroundThemeCategory(parseFormBackgroundTheme(formObject.backgroundTheme)),
        form_created_at: formObject.createdAtUtc,
        title_font_style: formObject.titleFontStyle || null,
        suggested_amounts_experiment_data: suggestedAmountsExperimentData?.experimentVariant,
        suggested_amounts_displayed: suggestedAmountsExperimentData?.experimentOutput,
        form_has_addons: availableAddOnsCount > 0,
        available_addons_count: availableAddOnsCount,
        items_count: itemsCount,
        addons_count: addOnsCount,
        ...suggestedAmountsTracking,
        recurrence_interval: donationSelection?.recurrenceInterval,
        amount_source: donationSelection?.amountSource,
        charge_day_customized: donationSelection?.chargeDayCustomized,
      });
    },
    [
      hasFormStateErrors,
      formStateErrorMessage,
      logAmplitudeEvent,
      formAmplitudeContext,
      commandId,
      category,
      formObject,
      displayAddressQuestion,
      donationFormForAmplitude,
      isDonationForEvent,
      ticketing.extraDonation,
      ticketing.thermometerTarget,
      availableAddOnsCount,
      itemsCount,
      addOnsCount,
      isEmbed,
      isoLocale,
      locale,
      organization?.country,
      tip,
      selectedTip.percentage,
      getTipChoice,
      displayedFormAmount,
      selectedPaymentMethod,
      currency,
      expressCheckoutWallet,
      impactChainUtmSourceEmail,
      source,
      suggestedAmountsExperimentData,
      getDonationSelection,
    ]
  );

  return {
    logDonorFormSubmitted,
  };
}
